import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import BulletList from "@tiptap/extension-bullet-list";
import ListItem from "@tiptap/extension-list-item";
import Paragraph from "@tiptap/extension-paragraph";
import OrderedList from "@tiptap/extension-ordered-list";
import { stripHtml } from "string-strip-html";

import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Link as LinkIcon,
  Heading1,
  Quote,
  Undo,
  Redo,
} from "lucide-react";

import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

const ToolbarButton = ({
  onClick,
  icon: Icon,
  isActive = false,
}: {
  onClick: () => void;
  icon: React.ElementType;
  isActive?: boolean;
}) => (
  <button
    onClick={onClick}
    type="button"
    className={cn(
      "h-8 w-8 p-2 rounded hover:bg-neutral-100 text-neutral-600 transition-colors",
      isActive && "bg-neutral-200"
    )}
  >
    <Icon size={16} />
  </button>
);

export const ProjectDescriptionEditor = ({
  value,
  setValue,
  placeholder = "",
  maxLength = 50,
}: {
  value: string;
  setValue: (val: string) => void;
  placeholder?: string;
  maxLength?: number;
}) => {
  const { t } = useTranslation();
  const lastValidContent = useRef(value);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      BulletList,
      ListItem,
      OrderedList,
      Paragraph,
      Link.configure({ openOnClick: true }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class:
          "min-h-[120px] px-4 py-2 text-neutral-n700 font-normal outline-none",
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const visibleText = stripHtml(html).result;

      if (visibleText.length <= maxLength) {
        lastValidContent.current = html;
        setValue(html);
      } else {
        editor.commands.setContent(lastValidContent.current);
      }
    },
  });

  useEffect(() => {
    if (editor && value !== lastValidContent.current) {
      lastValidContent.current = value;
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  useEffect(() => {
    return () => editor?.destroy();
  }, [editor]);

  if (!editor) return null;

  const visibleLength = stripHtml(value).result.length;

  return (
    <div className="flex flex-col gap-1 relative">
      <span className="text-[#00000080] text-[14px] font-medium bg-white absolute top-[-12px] left-[14px] z-100 px-1">
        {placeholder}
      </span>
      <div className="border-2 border-[#00000033] w-full rounded-[8px] overflow-hidden shadow-sm">
        <div className="flex items-center justify-between px-2 py-1 border-b-2 border-[#00000033]">
          <div className="flex items-center gap-1">
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleBold().run()}
              icon={Bold}
              isActive={editor.isActive("bold")}
            />
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleItalic().run()}
              icon={Italic}
              isActive={editor.isActive("italic")}
            />
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              icon={List}
              isActive={editor.isActive("bulletList")}
            />
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              icon={ListOrdered}
              isActive={editor.isActive("orderedList")}
            />
            <ToolbarButton
              onClick={() => {
                const url = prompt("Enter URL");
                if (url) editor.chain().focus().setLink({ href: url }).run();
              }}
              icon={LinkIcon}
            />
            <ToolbarButton
              onClick={() =>
                editor.chain().focus().toggleHeading({ level: 1 }).run()
              }
              icon={Heading1}
              isActive={editor.isActive("heading", { level: 1 })}
            />
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              icon={Quote}
              isActive={editor.isActive("blockquote")}
            />
          </div>
          <div className="flex gap-1">
            <ToolbarButton
              onClick={() => editor.chain().focus().undo().run()}
              icon={Undo}
            />
            <ToolbarButton
              onClick={() => editor.chain().focus().redo().run()}
              icon={Redo}
            />
          </div>
        </div>
        <EditorContent editor={editor} />
      </div>
      {maxLength && (
        <span className="text-[15px] font-medium text-[#00000080] self-end">
          {t("symbols")} {maxLength - visibleLength}
        </span>
      )}
    </div>
  );
};
